import {DoubleArrow} from '@mui/icons-material'
import CachedIcon from '@mui/icons-material/Cached'
import DownloadDoneIcon from '@mui/icons-material/DownloadDone'
import {Button, Chip, Divider} from '@mui/material'
import dayjs from 'dayjs'
import {envImagePath} from 'GLOBAL/envVariables'
import InstallAutocomplete from 'pages/install/layout/InstallAutocomplete'
import {useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import {getInstallList, imageDownload, imageRefresh} from 'utils/apiCalling/api'
import {globalSerialNumberFormatWithHyphens, installMethodEnum} from 'utils/const/globalConst'
import {getInstallDecisionColor, getSignalGrade} from 'utils/func/justUtils'
import {installDevicedBmToolTip} from 'utils/toolTips/toolTipText'

const SelectInstallableDevice = (props: any) => {
  const {set, setRenderStep, step, installSubmit, getPosition} = props
  const userInfo = useSelector((state: any) => state?.userInfo?.userInfo)
  const position = useSelector((state: any) => state?.currentPosition.position)
  const [installableList, setInstallableList] = useState<any>(null)
  const [refreshLoading, setRefreshLoading] = useState<boolean>(false)
  const [selectInstallType, setSelectInstallType] = useState<any>(null)
  /* 측정된 전파 세기(RSRP) x 품질(RSRQ) 로 산출한 신호 등급 (미수신이면 null) */
  const signalGrade = getSignalGrade(
    installSubmit?.rssiStatus != null ? Number(installSubmit.rssiStatus) : null,
    installSubmit?.linkquality != null ? Number(installSubmit.linkquality) : null
  )

  const getInstallListCall = () => {
    getInstallList(userInfo, 'installDevice', null, {
      success: (res: any) => {
        setInstallableList(res)
      },
      fail: (err: any) => {
        console.log(err)
      },
      loading: (loading: boolean) => {},
    })
  }

  const refreshDeviceSignal = (imei: any) => {
    getInstallList(userInfo, 'installDevice', null, {
      success: (res: any) => {
        setInstallableList(res)
        const currentDevice = res?.find((d: any) => d.imei === imei)
        if (currentDevice) {
          set((prev: any) => ({
            ...prev,
            rssiStatus: currentDevice.rssiStatus ?? prev?.rssiStatus,
            temperature: currentDevice.temperature ?? prev?.temperature,
            linkquality: currentDevice.linkquality ?? prev?.linkquality,
          }))
        }
      },
      fail: (err: any) => console.log(err),
      loading: () => {},
    })
  }

  const imageRefreshCall = () => {
    imageRefresh(installSubmit, {
      success: (res: any) => {
        getPosition()
        /* 새로고침은 '최신 사진 재요청'이라 응답이 비어 있으면 새 사진이 없다는 뜻이다.
           기존 이미지 정보를 지우면 화면과 다운로드가 함께 깨지므로 이전 값을 유지한다. */
        set((prev: any) => ({
          ...prev,
          imei: res?.imei ?? prev?.imei,
          idxDate: res?.idxDate ?? prev?.idxDate,
          filePath: res?.filePath ?? prev?.filePath,
          createAt: res?.createAt ?? prev?.createAt,
          rssiStatus: res?.rssiStatus ?? prev?.rssiStatus,
          temperature: res?.temperature ?? prev?.temperature,
          linkquality: res?.linkquality ?? prev?.linkquality,
        }))
      },
      fail: (err: any) => {
        console.log(err)
        setRefreshLoading(false)
      },
      loading: (loading: boolean) => {
        setRefreshLoading(loading)
        if (!loading) refreshDeviceSignal(installSubmit?.imei)
      },
    })
  }

  useEffect(() => {
    if (step === 1) {
      getInstallListCall()
    }
  }, [step])

  useEffect(() => {
    setSelectInstallType(null)
  }, [installSubmit])

  useEffect(() => {
    set({...installSubmit, latitude: position.latitude, longitude: position.longitude})
  }, [position])

  return (
    <div className={'row mx-0 justify-content-center pb-3'}>
      <InstallAutocomplete
        id='choose-device'
        label='단말기 선택'
        placeholder='단말기를 선택해주세요.'
        options={installableList ?? []}
        getOptionLabel={(option: any) =>
          `${globalSerialNumberFormatWithHyphens(option?.serialNumber)} - ${option?.deviceTypeName}`
        }
        filterOptions={(options, {inputValue}) => {
          const query = inputValue.replace(/-/g, '').toLowerCase()
          return options.filter(
            (option: any) =>
              option?.serialNumber?.replace(/-/g, '').toLowerCase().includes(query) ||
              option?.deviceTypeName?.toLowerCase().includes(query)
          )
        }}
        isOptionEqualToValue={(option: any, value: any) =>
          option.serialNumber === value.serialNumber
        }
        onChange={(rowValue) => {
          set((prev: any) => ({
            ...prev,
            brightness: Number(rowValue?.brightness),
            contrast: Number(rowValue?.contrast),
            createAt: rowValue?.createAt,
            deviceType: rowValue?.deviceType,
            deviceTypeName: rowValue?.deviceTypeName,
            filePath: rowValue?.filePath,
            imei: rowValue?.imei,
            idxDate: rowValue?.idxDate,
            serialNumber: rowValue?.serialNumber,
            rssiStatus: rowValue?.rssiStatus,
            temperature: rowValue?.temperature,
            linkquality: rowValue?.linkquality,
          }))
        }}
      />

      {installSubmit?.imei && (
        <div className={'my-2 p-2 border border-gray-400 rounded-2'}>
          {refreshLoading ? (
            <div className={'d-flex justify-content-center align-items-center my-5'}>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {installSubmit?.deviceType === 'analog' ? (
                <>
                  <div className={'w-100'}>
                    <img
                      src={`${envImagePath}${installSubmit?.filePath}`}
                      alt={'device'}
                      loading='lazy'
                      style={{width: '100%', maxWidth: 320, display: 'block', margin: '0 auto'}}
                    />
                  </div>

                  <div className={'mt-3'}>
                    <div className={'d-flex align-items-baseline justify-content-between mb-1'}>
                      <span className={'fs-4 fw-bold'}>마지막 통신 시각</span>
                      <span className={'fs-3 fw-bold'}>
                        {dayjs(installSubmit?.createAt).format('YY.MM.DD HH시 mm분 ss초')}
                      </span>
                    </div>
                    <Divider className={'my-2'} />
                    <div className={'d-flex flex-wrap align-items-center gap-2'}>
                      <span
                        style={{
                          backgroundColor: getInstallDecisionColor(
                            installSubmit?.rssiStatus != null
                              ? Number(installSubmit.rssiStatus)
                              : null,
                            installSubmit?.linkquality != null
                              ? Number(installSubmit.linkquality)
                              : null
                          ),
                          color: 'white',
                          padding: '6px 16px',
                          borderRadius: '20px',
                          fontWeight: 'bold',
                          fontSize: '1.4rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '10px',
                        }}
                      >
                        전파 세기 : {installSubmit?.rssiStatus}dBm
                        <span style={{opacity: 0.5}}>|</span>
                        {installSubmit?.linkquality ?? '-'}
                      </span>

                      {Number(installSubmit?.temperature || 99) < 90 && (
                        <Chip
                          label={`온도 : ${installSubmit?.temperature}°C`}
                          color={'success'}
                          variant={'filled'}
                          sx={{fontSize: '1.3rem', height: 32}}
                        />
                      )}
                    </div>
                  </div>

                  <div className={'alert alert-dark mt-2'}>
                    <span className={'fs-3 fw-bold'}>설치 방식을 선택해주세요</span>
                    <div className={'d-flex justify-content-around align-items-end mt-2'}>
                      <Chip
                        label={`${installMethodEnum.INSIDE}`}
                        color={'primary'}
                        variant={
                          selectInstallType === installMethodEnum.INSIDE ? 'filled' : 'outlined'
                        }
                        className={'fs-4'}
                        onClick={() => setSelectInstallType(installMethodEnum.INSIDE)}
                      />

                      <Chip
                        label={`${installMethodEnum.OUTSIDE}`}
                        color={'primary'}
                        variant={
                          selectInstallType === installMethodEnum.OUTSIDE ? 'filled' : 'outlined'
                        }
                        className={'fs-4'}
                        onClick={() => setSelectInstallType(installMethodEnum.OUTSIDE)}
                      />
                    </div>
                  </div>
                  {installDevicedBmToolTip(signalGrade, selectInstallType)}

                  <div className={'my-2 d-flex gap-2'}>
                    <Button
                      variant={'contained'}
                      color={'warning'}
                      fullWidth
                      startIcon={<CachedIcon />}
                      onClick={() => imageRefreshCall()}
                    >
                      새로고침
                    </Button>
                    <Button
                      variant={'contained'}
                      color={'success'}
                      fullWidth
                      startIcon={<DownloadDoneIcon />}
                      disabled={!installSubmit?.filePath}
                      onClick={async () =>
                        await imageDownload(
                          installSubmit?.filePath,
                          installSubmit?.brightness || 32,
                          installSubmit?.contrast || 255,
                          installSubmit?.rotate || 0,
                          installSubmit?.serialNumber
                        )
                      }
                    >
                      다운로드
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <h5 className={'text-center text-danger'}>디지털 단말기입니다.</h5>
                  <div className={'mt-3'}>
                    <div className={'d-flex align-items-baseline justify-content-between mb-1'}>
                      <span className={'fs-4 fw-bold'}>마지막 통신 시각</span>
                      <span className={'fs-3 fw-bold'}>
                        {dayjs(installSubmit?.createAt).format('YY.MM.DD HH시 mm분 ss초')}
                      </span>
                    </div>
                    <Divider className={'my-2'} />
                    <div className={'d-flex flex-wrap align-items-center gap-2'}>
                      <span
                        style={{
                          backgroundColor: getInstallDecisionColor(
                            installSubmit?.rssiStatus != null
                              ? Number(installSubmit.rssiStatus)
                              : null,
                            installSubmit?.linkquality != null
                              ? Number(installSubmit.linkquality)
                              : null
                          ),
                          color: 'white',
                          padding: '6px 16px',
                          borderRadius: '20px',
                          fontWeight: 'bold',
                          fontSize: '1.4rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '10px',
                        }}
                      >
                        전파 : {installSubmit?.rssiStatus}
                        <span style={{opacity: 0.5}}>|</span>
                        {installSubmit?.linkquality ?? '-'}
                      </span>

                      {Number(installSubmit?.temperature || 99) < 90 && (
                        <Chip
                          label={`온도 : ${installSubmit?.temperature}°C`}
                          color={'success'}
                          variant={'filled'}
                          sx={{fontSize: '1.3rem', height: 32}}
                        />
                      )}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          background: '#fff',
          paddingTop: 8,
          paddingBottom: 8,
          width: '100%',
        }}
      >
        <Button
          variant={'contained'}
          color={'primary'}
          fullWidth
          disabled={installSubmit?.imei === null || selectInstallType === null}
          endIcon={<DoubleArrow />}
          onClick={() => {
            if (installSubmit?.deviceType === 'analog') {
              setRenderStep(2)
            } else if (installSubmit?.deviceType === 'digital') {
              setRenderStep(3)
              set((prev: any) => ({
                ...prev,
                idxDate: '20230626',
                xstart: 0,
                ystart: 0,
                xend: 0,
                yend: 0,
                recognizedType: null,
                rotate: 0,
              }))
            }
          }}
        >
          단말기 선택 완료
        </Button>
      </div>
    </div>
  )
}
export default SelectInstallableDevice
